import { useEffect, useState } from 'react';
import { ArrowLeft, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';
import type { PublicProfessional } from '@/hooks/usePublicBooking';

interface PublicReview {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  client_full_name: string | null;
  service_name: string | null;
}

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

function Stars({ value, size = 15 }: { value: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, lineHeight: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ fontSize: size, color: i <= Math.round(value) ? '#f59e0b' : 'var(--pb-border)' }}>
          ★
        </span>
      ))}
    </span>
  );
}

interface ProProfilePageProps {
  professional: PublicProfessional;
  salonName: string;
  themeVars: React.CSSProperties;
  onBack: () => void;
  onBook: (proId: string) => void;
}

export function ProProfilePage({ professional, salonName, themeVars, onBack, onBook }: ProProfilePageProps) {
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    // RPC en vez de un select con join a clients: la reserva pública no
    // tiene sesión (rol anon) y clients no tiene ninguna policy de SELECT
    // para anon -- el join siempre volvía null, por eso toda reseña
    // mostraba "Cliente" genérico. get_public_reviews_for_professional
    // devuelve solo el nombre del cliente (no el resto de su fila).
    supabase
      .rpc('get_public_reviews_for_professional', { p_professional_id: professional.id })
      .then(({ data }) => {
        setReviews((data ?? []) as PublicReview[]);
        setLoadingReviews(false);
      });
  }, [professional.id]);

  const roleLabel = professional.job_title?.trim() || 'Profesional';
  const avg = professional.average_rating ?? null;
  const total = professional.total_reviews ?? 0;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 9000,
      background: 'var(--pb-bg)',
      fontFamily: 'var(--pb-font-body)',
      overflowY: 'auto',
      display: 'flex', flexDirection: 'column',
      // themeVars applied directly so este elemento define su propio scope de variables
      ...themeVars,
    } as React.CSSProperties}>

      {/* ── Nav bar ──────────────────────────────────────────────── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 9010,
        background: 'var(--pb-bg)',
        borderBottom: '1px solid var(--pb-border)',
        display: 'flex', alignItems: 'center',
        padding: '0 16px', height: 56,
        gap: 12,
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--pb-text)', fontFamily: 'var(--pb-font-body)',
            fontSize: 13, fontWeight: 600, padding: '6px 0',
          }}
        >
          <ArrowLeft size={17} />
          Nuestro equipo
        </button>
        <p style={{
          flex: 1, textAlign: 'center',
          margin: 0, fontSize: 13, fontWeight: 600,
          color: 'var(--pb-text-muted)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {salonName}
        </p>
        {/* spacer para centrar el salon name */}
        <div style={{ width: 120 }} />
      </div>

      {/* ── Contenido ─────────────────────────────────────────────── */}
      <div style={{ flex: 1, paddingBottom: 100 }}>

        {/* Header: avatar + nombre + rating */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '36px 24px 28px',
          borderBottom: '1px solid var(--pb-border)',
        }}>
          <div style={{
            width: 100, height: 100, borderRadius: '50%',
            overflow: 'hidden', marginBottom: 16,
            border: '2px solid var(--pb-pro-photo-border)',
            background: professional.color ?? 'var(--pb-pro-initials-bg)',
            flexShrink: 0,
          }}>
            {professional.avatar_url
              ? <img src={professional.avatar_url} alt={professional.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 30, fontWeight: 700, color: 'var(--pb-pro-initials-color)' }}>
                    {getInitials(professional.full_name)}
                  </span>
                </div>
            }
          </div>

          <h1 style={{
            margin: '0 0 8px',
            fontFamily: 'var(--pb-font-heading)',
            fontSize: 'clamp(22px, 5vw, 28px)',
            fontWeight: 700,
            color: 'var(--pb-text)',
            textAlign: 'center',
          }}>
            {professional.full_name}
          </h1>

          {/* Rol -- reemplaza la lista completa de servicios que mostraba
              antes acá; el rol ya alcanza como presentación, la lista de
              servicios real se ve al elegir el profesional en la reserva. */}
          <p style={{
            margin: '0 0 10px',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--pb-text-muted)',
            fontStyle: 'italic',
          }}>
            {roleLabel}
          </p>

          {avg ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Stars value={avg} size={16} />
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--pb-text)' }}>{avg.toFixed(1)}</span>
              <span style={{ fontSize: 13, color: 'var(--pb-text-muted)' }}>
                ({total} reseña{total !== 1 ? 's' : ''})
              </span>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--pb-text-muted)' }}>Sin calificaciones aún</p>
          )}

          {/* Bio */}
          {professional.notes && (
            <p style={{
              margin: '16px 0 0',
              maxWidth: 480,
              textAlign: 'center',
              fontSize: 14,
              color: 'var(--pb-text-secondary)',
              lineHeight: 1.7,
            }}>
              {professional.notes}
            </p>
          )}
        </div>

        {/* Reseñas */}
        <div style={{ padding: '24px', maxWidth: 640, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          <p style={{
            margin: '0 0 16px', fontSize: 11, fontWeight: 700,
            color: 'var(--pb-text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.12em',
          }}>
            Reseñas de clientes
          </p>

          {loadingReviews ? (
            <p style={{ fontSize: 13, color: 'var(--pb-text-muted)', padding: '20px 0' }}>
              Cargando reseñas...
            </p>
          ) : reviews.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--pb-text-muted)', padding: '20px 0' }}>
              Aún no hay reseñas publicadas para este profesional.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {reviews.map((r) => (
                <div
                  key={r.id}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid var(--pb-border)',
                    borderRadius: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: r.comment ? 6 : 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 600, color: 'var(--pb-text)' }}>
                        {r.client_full_name ?? 'Cliente'}
                      </p>
                      {r.service_name && (
                        <p style={{ margin: 0, fontSize: 11, color: 'var(--pb-text-muted)' }}>
                          {r.service_name}
                        </p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <Stars value={r.rating} size={12} />
                      <p style={{ margin: '3px 0 0', fontSize: 10, color: 'var(--pb-text-muted)' }}>
                        {formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale: es })}
                      </p>
                    </div>
                  </div>
                  {r.comment && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--pb-text)', lineHeight: 1.55, opacity: 0.85 }}>
                      "{r.comment}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── CTA fijo abajo — mismo patrón que la sticky bar del booking ── */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9001,
        background: 'var(--pb-sticky-bg)',
        padding: '12px 20px 28px',
        display: 'flex', gap: 10,
      }}>
        <button
          onClick={onBack}
          style={{
            flex: '0 0 auto',
            padding: '13px 16px',
            background: 'transparent',
            color: 'var(--pb-sticky-btn-bg)',
            border: '1px solid var(--pb-sticky-btn-bg)',
            borderRadius: 'var(--pb-sticky-btn-radius)',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'var(--pb-font-body)',
            display: 'flex', alignItems: 'center', gap: 6,
            opacity: 0.8,
          }}
        >
          <ArrowLeft size={15} />
          Ver más
        </button>
        <button
          onClick={() => onBook(professional.id)}
          style={{
            flex: 1,
            padding: '13px 0',
            background: 'var(--pb-sticky-btn-bg)',
            color: 'var(--pb-sticky-btn-color)',
            border: 'none',
            borderRadius: 'var(--pb-sticky-btn-radius)',
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'var(--pb-font-body)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            letterSpacing: '0.02em',
          }}
        >
          <Calendar size={16} />
          Reservar con {professional.full_name.split(' ')[0]}
        </button>
      </div>
    </div>
  );
}
